import type { Clock, TimerHandle } from "./ports";

/**
 * The Pay button's second life, as time: once pressed it tells the
 * payment's status one step at a time, each held long enough to read, and
 * the checkout around it folds away a beat after the press. Every renderer
 * draws these same steps on these same timings.
 */
export type PayStatus = "processing" | "accepted" | "issuing" | "done";

/**
 * Pressing pay, in beats:
 * - `inline`: the spinner and "Processing payment" in the button;
 * - `folding`: the rest of the checkout folds away around the status.
 */
export type PayPhase = "inline" | "folding";

export const PAY_STATUS_ORDER: ReadonlyArray<PayStatus> = [
  "processing",
  "accepted",
  "issuing",
  "done",
];

/** How long each step stays, at least, so a fast payment still reads. */
export const HOLD_MS = 950;
/** How long the spinner sits in the button before the checkout folds. */
export const FOLD_DELAY_MS = 420;
/** How long the checkout (and the sheet with it) takes to fold away. */
export const FOLD_MS = 500;

export type PayView = { status: PayStatus | null; phase: PayPhase | null };

/**
 * The status walks toward `target` one step at a time with a minimum hold
 * each; `null` (a refused payment, nothing pressed) clears it at once.
 * `onDone` fires once "done" has been shown for a hold. Any change of target
 * restarts the pending hold, as a new target is news worth its full beat.
 */
export function createPayTimeline(input: {
  clock: Clock;
  onChange: (view: PayView) => void;
  onDone: () => void;
}) {
  const { clock } = input;
  let target: PayStatus | null = null;
  let shown: PayStatus | null = null;
  let phase: PayPhase = "inline";
  let doneFired = false;
  let stepTimer: TimerHandle | null = null;
  let foldTimer: TimerHandle | null = null;
  let disposed = false;

  const view = (): PayView => ({
    status: shown,
    phase: shown !== null ? phase : null,
  });
  const clear = (timer: TimerHandle | null) => {
    if (timer !== null) clock.clearTimeout(timer);
    return null;
  };

  const show = (next: PayStatus | null) => {
    if (disposed || next === shown) return;
    const wasOn = shown !== null;
    shown = next;
    input.onChange(view());
    step();
    if (wasOn !== (shown !== null)) fold();
  };

  /** Toward the target, or the hold before "done" is announced. */
  const step = () => {
    stepTimer = clear(stepTimer);
    if (target === null) {
      doneFired = false;
      show(null);
      return;
    }
    if (shown === null) {
      show("processing");
      return;
    }
    const from = PAY_STATUS_ORDER.indexOf(shown);
    const to = PAY_STATUS_ORDER.indexOf(target);
    if (from < to) {
      stepTimer = clock.setTimeout(
        () => show(PAY_STATUS_ORDER[from + 1]),
        HOLD_MS,
      );
      return;
    }
    if (shown === "done" && !doneFired)
      stepTimer = clock.setTimeout(() => {
        doneFired = true;
        input.onDone();
      }, HOLD_MS);
  };

  /** Pressed: inline at once, folding a beat later. */
  const fold = () => {
    foldTimer = clear(foldTimer);
    const changed = phase !== "inline";
    phase = "inline";
    if (changed && shown !== null) input.onChange(view());
    if (shown === null) return;
    foldTimer = clock.setTimeout(() => {
      phase = "folding";
      input.onChange(view());
    }, FOLD_DELAY_MS);
  };

  return {
    view,
    setTarget(next: PayStatus | null) {
      if (disposed || next === target) return;
      target = next;
      step();
    },
    /** Stopped: no step or fold fires until `resume`. */
    pause() {
      stepTimer = clear(stepTimer);
      foldTimer = clear(foldTimer);
    },
    /** Started again: carry on from where the status stands. */
    resume() {
      if (disposed) return;
      step();
      if (shown !== null && phase === "inline") fold();
    },
    dispose() {
      disposed = true;
      stepTimer = clear(stepTimer);
      foldTimer = clear(foldTimer);
    },
  };
}
